import { clampRgba, isRgba, type Rgba } from '../color.ts';
import { createSkiffBoat } from './document.ts';
import {
  FACTORY_ACCENT,
  FACTORY_DECK,
  FACTORY_HULL,
  normalizeBoat,
} from './limits.ts';
import { fittingsFromUnknown } from './fittings.ts';
import { sideViewColorsFromLivery } from './sideView.ts';
import { sanitizeBoatName } from './name.ts';
import type { BoatDocument, SideViewColors } from './types.ts';
import { CURRENT_BOAT_VERSION, OLDEST_SUPPORTED_BOAT_VERSION } from './version.ts';

export { CURRENT_BOAT_VERSION, OLDEST_SUPPORTED_BOAT_VERSION };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asColor(value: unknown, fallback: Rgba): Rgba {
  if (!isRgba(value)) {
    return { ...fallback };
  }

  return clampRgba(value);
}

function sideColors(
  value: Record<string, unknown>,
  legacy: { hull: Rgba; deck: Rgba; accent: Rgba },
): SideViewColors {
  const defaults = sideViewColorsFromLivery(legacy);
  const raw = isRecord(value.side) ? value.side : {};
  const side = {} as SideViewColors;

  for (const key of Object.keys(defaults) as (keyof SideViewColors)[]) {
    side[key] = asColor(raw[key], defaults[key]);
  }

  return side;
}

/**
 * Forward-migrates a boat file. v1 stored hull, deck, and accent. Those three still
 * paint `boat.svg`. v2 adds one colour per side-view fill; a missing part takes the
 * colour that v1 already painted there, and the keel stays the authored grey.
 * v3 adds `fittings`. A missing flag takes that fitting's default (on, for the
 * rail, wings, and side window a v2 card already drew).
 * `version` above current, or not a boat at all, is `null`.
 */
export function migrateBoatDocument(value: unknown): BoatDocument | null {
  if (!isRecord(value)) {
    return null;
  }

  const version = value.version;

  if (typeof version !== 'number' || !Number.isFinite(version)) {
    return null;
  }

  if (version < OLDEST_SUPPORTED_BOAT_VERSION || version > CURRENT_BOAT_VERSION) {
    return null;
  }

  const factory = createSkiffBoat();
  const boat: BoatDocument = {
    version: CURRENT_BOAT_VERSION,
    name: typeof value.name === 'string' ? sanitizeBoatName(value.name) : factory.name,
    lengthM: asNumber(value.lengthM, factory.lengthM),
    beamM: asNumber(value.beamM, factory.beamM),
    draftM: asNumber(value.draftM, factory.draftM),
    planingSpeedKmh: asNumber(value.planingSpeedKmh, factory.planingSpeedKmh),
    turnM: asNumber(value.turnM, factory.turnM),
    accelM: asNumber(value.accelM, factory.accelM),
    brakeM: asNumber(value.brakeM, factory.brakeM),
    hull: asColor(value.hull, FACTORY_HULL),
    deck: asColor(value.deck, FACTORY_DECK),
    accent: asColor(value.accent, FACTORY_ACCENT),
    side: sideColors(value, {
      hull: asColor(value.hull, FACTORY_HULL),
      deck: asColor(value.deck, FACTORY_DECK),
      accent: asColor(value.accent, FACTORY_ACCENT),
    }),
    fittings: fittingsFromUnknown(value.fittings),
  };

  normalizeBoat(boat);
  return boat;
}
